import { useCallback } from "react";
import useChangeTooltipMsg from "../useChangeTooltipMsg";

interface UseCopyToClipboardOptions {
  defaultLabel?: string;
  copiedLabel?: string;
}

interface UseCopyToClipboardReturnValue {
  copyLabel: string;
  copyText: (text: string) => Promise<void>;
  copyRichText: (plainText: string, htmlContent: string) => Promise<void>;
}

const useCopyToClipboard = (
  { defaultLabel = "Copy", copiedLabel = "Copied!" }: UseCopyToClipboardOptions = {}
): UseCopyToClipboardReturnValue => {
  const { copyMsg: copyLabel, setCopyMsg } = useChangeTooltipMsg(
    defaultLabel,
    copiedLabel
  );

  const copyText = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopyMsg(copiedLabel);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    },
    [setCopyMsg, copiedLabel]
  );

  const copyRichText = useCallback(
    async (plainText: string, htmlContent: string) => {
      try {
        const textBlob = new Blob([plainText], { type: "text/plain" });
        const htmlBlob = new Blob([htmlContent], { type: "text/html" });

        await navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": textBlob,
            "text/html": htmlBlob,
          }),
        ]);
        setCopyMsg(copiedLabel);
      } catch (err) {
        console.error("Failed to copy rich text: ", err);
      }
    },
    [setCopyMsg, copiedLabel]
  );

  return { copyLabel, copyText, copyRichText };
};

export default useCopyToClipboard;
